import { useMemo, useState } from 'react';
import {
  PRODUCT_COLLECTION,
  parseDynamicProductList,
} from '@drc/pages/Home/components/DynamicProductRow/queries/ProductCollection';
import { toUpper } from 'lodash';
import useCountry from '@drc/hooks/useCountry';
import { useCurrency } from '@app/currency';
import useCustomerMarket from '@drc/hooks/useCustomerMarket';
import useEdgeQuery from '@app/data/edge/hooks/useEdgeQuery';
import { useAllAbVariants, useExperiment } from '@app/experiments';
import { EXPERIMENTS } from '@app/config';
import { LegacyProduct } from '@app/types';

interface ExtendedProductsProps {
  isExtendedHomepageCarousel: boolean;
  collection: string;
  page: number;
}

interface ExtendedProductsResult {
  extendedProducts: LegacyProduct[];
  loadingExtendedProducts: boolean;
}

const useExtendedProducts = ({
  isExtendedHomepageCarousel,
  collection,
  page,
}: ExtendedProductsProps): ExtendedProductsResult => {
  const variants = useAllAbVariants();
  const { currency: currencyCode } = useCurrency();
  const { marketName } = useCustomerMarket();
  const countryCode = useCountry();
  const isFavoriteTestEnabled =
    useExperiment(EXPERIMENTS.ab_pirate_product_cell_favorite_web_v1) ===
    'true';

  const [furthestPagination, setFurthestPagination] = useState(0);

  const { data, loading: loadingExtendedProducts } = useEdgeQuery(
    PRODUCT_COLLECTION,
    {
      enabled: isExtendedHomepageCarousel && page > furthestPagination,
      context: {
        headers: {
          'x-abtest-ids': variants.join(','),
        },
      },
      variables: {
        id: collection,
        limit: 6,
        country: toUpper(countryCode),
        currencyCode: toUpper(currencyCode),
        marketName,
        skipFavorite: !isFavoriteTestEnabled,
        page,
      },
      onSuccess: () => {
        if (page > furthestPagination) {
          setFurthestPagination(page);
        }
      },
    },
  );

  const extendedProducts = useMemo(
    () => parseDynamicProductList(data?.productCollection?.products, 6),
    [data?.productCollection?.products],
  );

  return {
    extendedProducts,
    loadingExtendedProducts,
  };
};

export default useExtendedProducts;
